"use client";
import React, { useState, useEffect } from "react";
import Image from "next/image";
import { ButtonSize, ButtonVariant, RegularButton } from "@/stories/button/button";
import ExternalLink from "@/app/assets/icons/external-arrow";
import Fingerprint from "@/app/assets/icons/finerprint";
import Home from "@/app/assets/icons/home";
import Diamond from "@/app/assets/icons/diamond";
import FD from "@/app/assets/icons/fd";
import Bullion from "@/app/assets/icons/bullion";
import Refer from "@/app/assets/icons/refer";
import Learn from "@/app/assets/icons/learn";
import Mail from "@/app/assets/icons/mail";
import Phone from "@/app/assets/icons/phone";
import GooglePlay from "@/app/assets/icons/googleplay";
import Close from "@/app/assets/icons/close";
import Menu from "@/app/assets/icons/menu";
import useRequest from "@/app/hooks/request.hook";
import { useRouter } from "next/navigation";
import { ICM_MAIL, ICM_PHN_1, ICM_PHN_2 } from "../utils/constants";
import { DirectPaths } from "@/config/routes";
import { setAnalyticsEvent } from "@/app/utils/SetAnalyticsEvent";
import Avatar from "@/app/assets/icons/avatar";
import SideDrawer from "./profile-drawer";
import PortfolioIcon from "@/app/assets/icons/portfolio-icon";
import { usePathname } from "next/navigation";
import { initBranch, trackEvent } from "../utils/branchio";
import GetICMAppModal from "@/app/components/get-icm-app-modal";
import FdAppDrawer from "../fd/components/fd-app-drawer";
import Media from "../assets/icons/media";
import { getXPartner } from "@/app/utils/helper";
import HeaderSkeleton from "./loaders/header-skeleton";
import { useAuth } from "@/context/authcontext";

interface NavItem {
    label: string;
    href: string;
    eventName?: string;
    branchEvent?: string;
    isActive?: boolean;
    isExternal?: boolean;
    isNew?: boolean;
    icon: React.ReactNode;
}

interface NavbarProps {
    active: string;
    navBarHidden?: boolean;
}

interface UserData {
    email: string;
    userId: string;
    verified: boolean;
    phoneVerified: string;
    phone: string;
    name: string;
    emailVerified: boolean;
    userType: string;
    referrer: string;
    success: boolean;
}

// Define proper types for API request states
interface ApiRequest {
    loading: boolean;
    error: Error | string | unknown | null; // Using 'any' here to allow any error type
}

interface ApiRequests {
    userEmail: ApiRequest;
    // Add more API request states as needed
}

const Navbar: React.FC<NavbarProps> = ({ active, navBarHidden }) => {
    const pathname = usePathname();
    const router = useRouter();
    const { isAuthenticated } = useAuth();
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState<boolean>(false);
    const { request } = useRequest(process.env.API_BASE_URL);
    const [userData, setUserData] = useState<UserData>();
    const [isLoggedIn, setIsLoggedIn] = useState<boolean>();
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [apiRequests, setApiRequests] = useState<ApiRequests>({
        userEmail: { loading: true, error: null },
    });
    const [isDrawerOpen, setIsDrawerOpen] = useState<boolean>(false);
    const [isHomePage, setIsHomePage] = useState<boolean>(false);
    const [isFdPage, setIsFdPage] = useState<boolean>(false);
    const [showAppModal, setShowAppModal] = useState<boolean>(false);
    const [isAppDrawerOpen, setIsAppDrawerOpen] = useState<boolean>(false);
    const [shouldShowNav, setShouldShowNav] = useState(true);

    const toggleDownloadApp = () => {
        setShowAppModal(!showAppModal);
        setIsAppDrawerOpen(!isAppDrawerOpen);
    };

    useEffect(() => {
        const searchParams =
            typeof window !== "undefined"
                ? Object.fromEntries(new URLSearchParams(window.location.search))
                : {};

        setShouldShowNav(Boolean(searchParams?.xPlatform || searchParams?.partner));
    }, []);

    useEffect(() => {
        const fetchUserEmail = async () => {
            // Set userEmail request to loading state
            setApiRequests((prev) => ({
                ...prev,
                userEmail: { loading: true, error: null },
            }));

            try {
                request({
                    url: "users/getUserEmail",
                    method: "GET",
                    headers: { "X-partner": getXPartner() },
                })
                    .then((response) => {
                        if (response) {
                            setUserData(response.data);
                            setIsLoggedIn(true);
                        } else {
                            setIsLoggedIn(false);
                        }
                        // Update loading state to false when done
                        setApiRequests((prev) => ({
                            ...prev,
                            userEmail: { loading: false, error: null },
                        }));
                    })
                    .catch((error) => {
                        console.error(error);
                        // Update with error and loading false
                        setApiRequests((prev) => ({
                            ...prev,
                            userEmail: { loading: false, error: error },
                        }));
                        setIsLoggedIn(false);
                    });
            } catch (error) {
                // Update with error and loading false
                setApiRequests((prev) => ({
                    ...prev,
                    userEmail: { loading: false, error: error },
                }));
                setIsLoggedIn(false);
            }
        };

        if (pathname == "/") {
            setIsHomePage(true);
        }

        if (pathname == "/fd") {
            setIsFdPage(true);
        }

        fetchUserEmail();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Check if any API request is still loading
    useEffect(() => {
        const isAnyApiLoading = Object.values(apiRequests).some((request) => request.loading);
        setIsLoading(isAnyApiLoading);
    }, [apiRequests]);

    const handleNavClick = (
        url: string,
        isMobileMenu: boolean,
        eventName?: string,
        branchEvent?: string,
        label?: string,
    ) => {
        if (isMobileMenu) {
            setIsMobileMenuOpen(false);
        }

        const plgPopup: { show: boolean; nav: string } = JSON.parse(
            window.sessionStorage.getItem("plgPopup") || "null",
        );
        if (plgPopup?.show === true) {
            plgPopup.nav = url;
            window.sessionStorage.setItem("plgPopup", JSON.stringify(plgPopup));
            window.history.go(-1);
            return;
        }

        const label_list = ["Media", "Learn"];

        if (eventName) {
            setAnalyticsEvent(url, eventName);
        }
        if (branchEvent) {
            initBranch()
                .then(() => {
                    trackEvent(
                        branchEvent,
                        userData?.email || "",
                        userData?.phone || "",
                        userData?.userId || "",
                    ).then(() => {
                        if (label_list.includes(String(label))) {
                            window.open(url);
                        } else {
                            window.location.href = url;
                        }
                    });
                })
                .catch((error) => {
                    console.error("Error initializing Branch:", error);
                    if (label_list.includes(String(label))) {
                        window.open(url);
                    } else {
                        window.location.href = url;
                    }
                });
        } else {
            if (label_list.includes(String(label))) {
                window.open(url);
            } else {
                window.location.href = url;
            }
        }
    };

    const navItems: NavItem[] = [
        {
            label: "Home",
            href: isLoggedIn
                ? String(`${process.env.ICM_URL}/platform`)
                : String(process.env.ICM_URL),
            isActive: Boolean(active === "Home"),
            isExternal: false,
            icon: <Home />,
        },
        {
            label: "Unlisted Shares",
            href: isLoggedIn
                ? String(`${process.env.ICM_STOCKS_URL}/platform`) || ""
                : String(`${process.env.ICM_URL}/unlisted-shares`),
            eventName: "Home_Nav_Bar_US",
            branchEvent: "Navigation_Shares",
            isActive: Boolean(active === "Stocks"),
            isExternal: false,
            icon: <Diamond />,
        },
        {
            label: "Gold",
            href: process.env.ICM_URL + "/bullion/gold/24-karat-gold" || "",
            isActive: Boolean(active === "bullion"),
            branchEvent: "Navigation_Bullion",
            isExternal: false,
            isNew: true,
            icon: <Bullion />,
        },
        {
            label: "FDs",
            href: process.env.ICM_FD_REDIRECT_URL + "fd" || "",
            isActive: Boolean(active === "FD"),
            branchEvent: "Navigation_FD",
            isExternal: false,
            icon: <FD />,
        },
        {
            label: "Portfolio",
            href: `${process.env.ICM_URL}/portfolio`,
            branchEvent: "Navigation_Portfolio",
            isActive: Boolean(active === "Portfolio"),
            isExternal: false,
            icon: <PortfolioIcon />,
        },
        {
            label: "Refer",
            href: `${process.env.ICM_URL}/refer`,
            eventName: "Refer_Homepage_Top_Nav_bar",
            branchEvent: "Navigation_Refer",
            isActive: Boolean(active === "Refer"),
            isExternal: false,
            icon: <Refer />,
        },
        {
            label: "Learn",
            href: `${process.env.ICM_LEARN}`,
            branchEvent: "Navigation_Learn",
            isActive: false,
            isExternal: false,
            icon: <Learn />,
        },
        {
            label: "About",
            href: `${process.env.ICM_URL}/about`,
            eventName: "Home_Nav_Bar_About_Us",
            isActive: Boolean(active === "About"),
            isExternal: false,
            icon: <Fingerprint />,
        },
        {
            label: "Media",
            href: `${process.env.ICM_MEDIA}`,
            eventName: "Home_Nav_Bar_Media",
            isActive: false,
            isExternal: false,
            icon: <Media />,
        },
    ];

    const handleLogin = (event: string) => {
        const isUE = window.location.href.includes(DirectPaths.UNLISTED_SHARES);

        // Get query string directly from window.location.search
        const currentQueryString = window.location.search;

        const eventName = isFdPage ? `FD_Top_Nav_Bar_${event}` : `Top_Nav_Bar_${event}`;

        initBranch()
            .then(() => {
                trackEvent(eventName, "", "");
            })
            .catch((error) => console.error("Error initializing Branch:", error));

        setAnalyticsEvent(window.location.origin, eventName);

        if (event === "Get_Started") {
            if (isUE) {
                // Preserve query parameters when redirecting to login
                window.location.href = `${process.env.ICM_STOCKS_URL}/login${currentQueryString}`;
            } else {
                // Preserve query parameters when using router
                router.push(`/login${currentQueryString}`);
            }
            return;
        }

        if (isUE) {
            // Preserve query parameters for other events (signup, etc.)
            window.location.href = `${process.env.ICM_STOCKS_URL}/${event.toLowerCase()}${currentQueryString}`;
        } else {
            // Preserve query parameters for other events
            router.push(`/${event.toLowerCase()}${currentQueryString}`);
        }
    };

    const handleLogoClick = (): void => {
        const plgPopup: { show: boolean; nav: string } = JSON.parse(
            window.sessionStorage.getItem("plgPopup") || "null",
        );
        if (plgPopup?.show === true) {
            plgPopup.nav = isAuthenticated ? DirectPaths.PLATFORM : DirectPaths.HOME;
            window.sessionStorage.setItem("plgPopup", JSON.stringify(plgPopup));
            window.history.go(-1);
            return;
        }

        let isUE = false;
        if (pathname.indexOf("/unlisted-shares") !== -1) {
            isUE = true;
        }
        const postUrl = process.env.ICM_URL + "/platform";
        const preUrl = isUE ? `${process.env.ICM_STOCKS_URL}/login` : process.env.ICM_URL || "/";

        if (!postUrl || !preUrl) {
            console.error("Environment variables ICM_POST_URL or ICM_URL are not properly defined");
            return;
        }

        window.location.href = isLoggedIn ? postUrl : preUrl;
    };

    useEffect(() => {
        if (isMobileMenuOpen) {
            document.body.style.overflow = "hidden";
        } else {
            document.body.style.overflow = "auto";
        }

        return () => {
            document.body.style.overflow = "auto";
        };
    }, [isMobileMenuOpen]);

    // Render loading skeleton
    if (isLoading && shouldShowNav) {
        return <HeaderSkeleton shouldShowNav={shouldShowNav} />;
    }

    return (
        <div
            id="navbar"
            className={`${shouldShowNav ? "hidden" : "flex"} h-[68px] items-center justify-center md:h-24`}
        >
            <div className="lg-[px-0] relative flex w-full gap-s6 px-4 sm:container md:px-0">
                {!navBarHidden && (
                    <div className="flex items-center sm:hidden">
                        <button
                            type="button"
                            onClick={() => setIsMobileMenuOpen((prevState) => !prevState)}
                            aria-controls="mobile-menu"
                            aria-expanded={isMobileMenuOpen}
                        >
                            <span className="sr-only">
                                {isMobileMenuOpen ? "Close menu" : "Open menu"}
                            </span>
                            <div className="flex h-5 w-6 flex-col items-center justify-between">
                                {isMobileMenuOpen ? <Close /> : <Menu />}
                            </div>
                        </button>
                    </div>
                )}
                <div className="flex w-full items-center justify-between py-s8 md:mx-s10">
                    <div className="flex flex-1 gap-20">
                        <div className="animate-fade" onClick={() => handleLogoClick()}>
                            <Image
                                src={`https://assets.incredmoney.com/images/webp/incred-money.webp`}
                                alt="InCred Money"
                                height={100}
                                width={100}
                                className="h-[32px] w-[90px] cursor-pointer sm:h-[42px] sm:w-[119px]"
                            />
                        </div>

                        {!navBarHidden && (
                            <div className="hidden flex-1 gap-s12 self-center sm:flex">
                                {navItems
                                    .filter(
                                        (item) =>
                                            item.label !== "Portfolio" ||
                                            (isLoggedIn && !isHomePage),
                                    )
                                    .map((item) => (
                                        <div
                                            key={item.label}
                                            onClick={() => {
                                                handleNavClick(
                                                    item.href,
                                                    false,
                                                    item.eventName,
                                                    item.branchEvent,
                                                    item.label,
                                                );
                                            }}
                                        >
                                            <div
                                                className={`${item?.isNew ? "" : ""} relative flex cursor-pointer items-center text-sm font-medium hover:text-gray-900 animate-fade ${
                                                    item.isActive
                                                        ? "text-slate-900"
                                                        : "text-slate-500"
                                                }`}
                                                aria-current={item.isActive ? "page" : undefined}
                                            >
                                                <div className="text-md font-semibold">
                                                    {item.label}
                                                </div>
                                                {item.isNew && (
                                                    <span className="absolute bottom-[16.5px] -right-[13px] animate-pulse text-[10px] font-semibold text-brand-primary-500">
                                                        NEW
                                                    </span>
                                                )}
                                                {item.isExternal && (
                                                    <span className="p3">
                                                        <div className="flex items-center gap-1">
                                                            <ExternalLink size={3} />
                                                        </div>
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                            </div>
                        )}
                    </div>
                    <SideDrawer
                        isOpen={isDrawerOpen}
                        onClose={() => setIsDrawerOpen(false)}
                        position="right"
                        name={userData?.name}
                        email={userData?.email}
                        userId={userData?.userId}
                        phone={userData?.phone}
                    />
                    {!navBarHidden &&
                        (isLoggedIn && !isHomePage ? (
                            <div onClick={() => setIsDrawerOpen(true)}>
                                <Avatar className="cursor-pointer" />
                            </div>
                        ) : (
                            <div className="flex items-center gap-s5 animate-fade">
                                <RegularButton
                                    noIcon
                                    showRightIcon={false}
                                    size={ButtonSize.Small}
                                    variant={ButtonVariant.SecondaryDefault}
                                    className="visible mr-s9 bg-white md:hidden"
                                    label="Get started"
                                    onClick={() => handleLogin("Get_Started")}
                                />

                                <RegularButton
                                    noIcon
                                    showRightIcon={false}
                                    size={ButtonSize.Small}
                                    variant={ButtonVariant.SecondaryDefault}
                                    className="hidden bg-white md:block"
                                    label="Log in"
                                    onClick={() => handleLogin("Login")}
                                />

                                <RegularButton
                                    noIcon
                                    showRightIcon={false}
                                    size={ButtonSize.Small}
                                    variant={ButtonVariant.SecondaryDefault}
                                    className="hidden bg-white md:block"
                                    label="Sign up"
                                    onClick={() => handleLogin("Signup")}
                                />
                            </div>
                        ))}
                </div>

                {/* Mobile Menu Overlay */}
                {isMobileMenuOpen && (
                    <div
                        className="bg-black/50 fixed inset-0 z-50"
                        onClick={() => setIsMobileMenuOpen(false)}
                    >
                        <div
                            className="absolute inset-y-0 left-0 flex w-full flex-col border border-black bg-white"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <div className="flex items-center justify-between border-b p-4">
                                <button onClick={() => setIsMobileMenuOpen(false)} className="p-2">
                                    <Close />
                                </button>
                                <RegularButton
                                    noIcon
                                    showRightIcon={false}
                                    size={ButtonSize.Small}
                                    variant={ButtonVariant.SecondaryDefault}
                                    className="bg-white"
                                    label="Get started"
                                    onClick={() => handleLogin("Get_Started")}
                                />
                            </div>

                            <div className="h-[calc(100vh-70px)] overflow-y-auto">
                                <ul className="px-6 py-4">
                                    {navItems.map((item) => (
                                        <li key={item.label} className="mb-6">
                                            <p
                                                // href={}
                                                className={`flex items-center text-base font-medium ${
                                                    item.isActive
                                                        ? "text-slate-900"
                                                        : "text-slate-500"
                                                }`}
                                                onClick={() =>
                                                    handleNavClick(
                                                        item.href,
                                                        true,
                                                        item.eventName,
                                                        item.branchEvent,
                                                        item.label,
                                                    )
                                                }
                                            >
                                                <span className="mr-4">{item.icon}</span>
                                                {item.label}
                                                {item.isExternal && (
                                                    <ExternalLink size={3} className="ml-2" />
                                                )}
                                                {item.isNew && (
                                                    <span className="relative bottom-3 right-2 animate-pulse text-xs font-semibold text-brand-primary-500">
                                                        NEW
                                                    </span>
                                                )}
                                            </p>
                                        </li>
                                    ))}
                                </ul>

                                <div className="border-t border-dashed px-6 py-6">
                                    <h3 className="mb-4 text-sm font-semibold text-neutral-400">
                                        HELP & SUPPORT
                                    </h3>
                                    <a
                                        href={`mailto:${ICM_MAIL}`}
                                        className="mb-4 flex items-center text-slate-600"
                                    >
                                        <span className="mr-4">
                                            <Mail />
                                        </span>
                                        {ICM_MAIL}
                                    </a>
                                    <div className="flex items-start text-slate-600">
                                        <span className="mr-4 mt-1">
                                            <Phone />
                                        </span>
                                        <address className="not-italic leading-relaxed">
                                            <a href={`tel:${ICM_PHN_1}`}>{ICM_PHN_1}</a>
                                            <br />
                                            <a href={`tel:${ICM_PHN_2}`}>{ICM_PHN_2}</a>
                                        </address>
                                    </div>
                                </div>

                                <div className="border-t border-dashed px-6 py-6">
                                    <h3 className="mb-4 text-sm font-semibold text-neutral-400">
                                        GET THE MOBILE APP
                                    </h3>
                                    <div className="flex items-center gap-4">
                                        <RegularButton
                                            showRightIcon={false}
                                            size={ButtonSize.Small}
                                            variant={ButtonVariant.SecondaryDefault}
                                            className="max-w-1/2 flex-1 bg-white"
                                            onClick={() =>
                                                window.open(process.env.ICM_APPLE_APP_URL)
                                            }
                                            label="iOS"
                                        />
                                        <RegularButton
                                            showRightIcon={false}
                                            icon={<GooglePlay />}
                                            size={ButtonSize.Small}
                                            variant={ButtonVariant.SecondaryDefault}
                                            className="max-w-1/2 flex-1 bg-white"
                                            onClick={() =>
                                                window.open(process.env.ICM_PLAYSTORE_URL)
                                            }
                                            label="Android"
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
            <GetICMAppModal open={showAppModal} onClose={toggleDownloadApp} />
            <FdAppDrawer isOpen={isAppDrawerOpen} onClose={toggleDownloadApp} />
        </div>
    );
};

export default Navbar;
